import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import FileAttachments from '../components/FileAttachments';
import { Thermometer, Plus, XCircle, CheckCircle2, ChevronDown, ChevronUp, Paperclip, FileText, Send, Trash2, Eye, AlertCircle, Pencil, RotateCcw } from 'lucide-react';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// ═══════════════════════════════════════════════════════════════
// MODAL: Generar nuevo informe mensual
// ═══════════════════════════════════════════════════════════════
function ModalGenerarInforme({ onClose, onGenerado }) {
  const hoy = new Date();
  const mesAnterior = hoy.getMonth() === 0 ? 12 : hoy.getMonth();
  const anioMesAnterior = hoy.getMonth() === 0 ? hoy.getFullYear() - 1 : hoy.getFullYear();
  
  const [anio, setAnio] = useState(anioMesAnterior);
  const [mes, setMes] = useState(mesAnterior);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerar = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.post('/informes/mensuales/generar', { tipo: 'temperatura', anio: parseInt(anio), mes: parseInt(mes) });
      onGenerado(res.data.informe);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al generar el informe');
    } finally { setLoading(false); }
  };

  // Generar lista de años (últimos 5)
  const anios = [];
  for (let a = hoy.getFullYear(); a >= hoy.getFullYear() - 4; a--) anios.push(a);

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={20} color="var(--color-primary)" /> Generar Informe Mensual
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger"><AlertCircle size={16} /> {error}</div>}
          <p style={{ color: 'var(--color-text-2)', marginBottom: 16, fontSize: '0.9rem' }}>
            Se generará un informe con todos los registros del mes seleccionado, incluyendo estadísticas y lista completa.
          </p>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Mes <span className="required">*</span></label>
              <select className="form-control" value={mes} onChange={e => setMes(e.target.value)}>
                {MESES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Año <span className="required">*</span></label>
              <select className="form-control" value={anio} onChange={e => setAnio(e.target.value)}>
                {anios.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGenerar} disabled={loading}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
            Generar Informe
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Ver detalle del informe (resumen + registros)
// ═══════════════════════════════════════════════════════════════
function ModalVerInforme({ informeId, onClose }) {
  const [informe, setInforme] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/informes/mensuales/${informeId}`).then(r => setInforme(r.data)).finally(() => setLoading(false));
  }, [informeId]);

  if (loading) return <div className="modal-overlay"><div className="modal"><div className="modal-body"><div className="spinner" /></div></div></div>;
  if (!informe) return null;

  const r = informe.datos_resumen || {};
  const registros = informe.datos_json || [];

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
              <FileText size={20} color="var(--color-primary)" /> {informe.titulo}
            </h2>
            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-3)', marginTop: 4 }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary)', fontWeight: 700 }}>{informe.codigo}</span>
              {' · '}
              <EstadoBadge estado={informe.estado} />
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {/* RESUMEN EJECUTIVO */}
          <h3 style={{ marginTop: 0, fontSize: '1.05rem' }}>📊 Resumen Ejecutivo</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
            <StatBox label="Total registros" valor={r.total_registros || 0} color="#3b82f6" />
            <StatBox label="T. Media" valor={r.temperatura?.media ? `${r.temperatura.media}°C` : '—'} color="#10b981" />
            <StatBox label="T. Máxima" valor={r.temperatura?.maxima ? `${r.temperatura.maxima}°C` : '—'} color="#f59e0b" />
            <StatBox label="T. Mínima" valor={r.temperatura?.minima ? `${r.temperatura.minima}°C` : '—'} color="#6366f1" />
            <StatBox label="Humedad media" valor={r.humedad?.media ? `${r.humedad.media}%` : '—'} color="#06b6d4" />
            <StatBox label="Fuera de rango" valor={r.registros_fuera_rango || 0} color="#dc2626" />
            <StatBox label="Cumplimiento" valor={`${r.porcentaje_cumplimiento || 100}%`} color="#10b981" />
          </div>

          {r.zonas_monitorizadas?.length > 0 && (
            <div style={{ marginBottom: 24, padding: 12, background: 'var(--color-gray-1)', borderRadius: 8 }}>
              <strong style={{ fontSize: '0.85rem' }}>Zonas monitorizadas:</strong>{' '}
              {r.zonas_monitorizadas.join(' · ')}
            </div>
          )}

          {/* LISTA COMPLETA */}
          <h3 style={{ fontSize: '1.05rem' }}>📋 Registros del mes ({registros.length})</h3>
          <div className="table-wrap" style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table>
              <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                <tr>
                  <th>Fecha</th><th>Hora</th><th>Zona</th>
                  <th>T. Actual</th><th>Humedad</th>
                  <th>Operario</th><th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {registros.map(reg => (
                  <tr key={reg.id} style={{ background: reg.estado === 'fuera_rango' ? 'rgba(220,38,38,.04)' : '' }}>
                    <td>{new Date(reg.fecha).toLocaleDateString('es-ES')}</td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{reg.hora}</td>
                    <td>{reg.zona}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{reg.temperatura_actual != null ? `${reg.temperatura_actual}°C` : '—'}</td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{reg.humedad != null ? `${reg.humedad}%` : '—'}</td>
                    <td style={{ fontSize: '0.82rem' }}>{reg.operario_nombre}</td>
                    <td style={{ fontSize: '0.8rem' }}>
                      {reg.estado === 'fuera_rango' ? <span style={{ color: 'var(--color-danger)' }}>⚠ Fuera</span> : 
                       reg.estado === 'incidencia' ? <span style={{ color: 'var(--color-warning)' }}>⚡ Incid.</span> :
                       <span style={{ color: 'var(--color-success)' }}>✓ OK</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {informe.fecha_firma && (
            <div style={{ marginTop: 20, padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
              <div style={{ fontWeight: 700, color: '#166534' }}>✅ Firmado por {informe.firmado_nombre}</div>
              <div style={{ fontSize: '0.85rem', color: '#15803d', marginTop: 4 }}>
                {new Date(informe.fecha_firma).toLocaleString('es-ES')}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Eliminar informe (doble confirmación)
// ═══════════════════════════════════════════════════════════════
function ModalEliminarInforme({ informe, onClose, onEliminado }) {
  const [codigo, setCodigo] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEliminar = async () => {
    setLoading(true); setError('');
    try {
      await api.delete(`/informes/mensuales/${informe.id}`, { data: { confirmacion_codigo: codigo, password } });
      onEliminado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-danger)' }}>
            <Trash2 size={20} /> Eliminar Informe
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-danger"><AlertCircle size={16} /> Esta acción no se puede deshacer fácilmente.</div>
          <p>Para confirmar, escribe el código del informe y tu contraseña.</p>
          <div className="form-group">
            <label className="form-label">Código del informe: <strong style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-mono)' }}>{informe.codigo}</strong></label>
            <input type="text" className="form-control" value={codigo} onChange={e => setCodigo(e.target.value)} placeholder={informe.codigo} />
          </div>
          <div className="form-group">
            <label className="form-label">Tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleEliminar} disabled={loading || !codigo || !password}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <Trash2 size={16} />}
            Eliminar definitivamente
          </button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// MODAL: Editar registro (admin + contraseña)
// ═══════════════════════════════════════════════════════════════
function ModalEditarRegistro({ registro, onClose, onActualizado }) {
  const [form, setForm] = useState({
    fecha: registro.fecha,
    hora: registro.hora || '',
    zona: registro.zona || 'Almacén principal',
    temperatura_min: registro.temperatura_min ?? '',
    temperatura_max: registro.temperatura_max ?? '',
    temperatura_actual: registro.temperatura_actual ?? '',
    humedad: registro.humedad ?? '',
    observaciones: registro.observaciones || ''
  });
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGuardar = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      await api.put(`/registros/temperatura/${registro.id}`, { ...form, password });
      onActualizado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={20} color="#3b82f6" /> Editar Registro
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
            <AlertCircle size={16} /> <strong>Acción restringida:</strong> Solo administradores. Se requiere contraseña.
          </div>
          
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Fecha</label>
              <input type="date" className="form-control" value={form.fecha} onChange={e => set('fecha', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Hora</label>
              <input type="time" className="form-control" value={form.hora} onChange={e => set('hora', e.target.value)} /></div>
          </div>
          <div className="form-group">
            <label className="form-label">Zona</label>
            <select className="form-control" value={form.zona} onChange={e => set('zona', e.target.value)}>
              <option>Almacén principal</option><option>Cámara fría</option>
              <option>Oficina</option><option>Zona recepción</option>
            </select>
          </div>
          <div className="form-grid-3">
            <div className="form-group"><label className="form-label">T. Mínima (°C)</label>
              <input type="number" step="0.1" className="form-control" value={form.temperatura_min} onChange={e => set('temperatura_min', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">T. Máxima (°C)</label>
              <input type="number" step="0.1" className="form-control" value={form.temperatura_max} onChange={e => set('temperatura_max', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">T. Actual (°C)</label>
              <input type="number" step="0.1" className="form-control" value={form.temperatura_actual} onChange={e => set('temperatura_actual', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Humedad (%)</label>
            <input type="number" step="0.1" className="form-control" value={form.humedad} onChange={e => set('humedad', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Observaciones</label>
            <textarea className="form-control" rows={2} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} /></div>

          <div className="form-group" style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
            <label className="form-label" style={{ color: '#92400e', fontWeight: 600 }}>🔐 Confirma con tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Tu contraseña actual" />
          </div>
          
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGuardar} disabled={loading || !password}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Eliminar registro (admin + contraseña)
// ═══════════════════════════════════════════════════════════════
function ModalEliminarRegistro({ registro, onClose, onEliminado }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEliminar = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      await api.delete(`/registros/temperatura/${registro.id}`, { data: { password } });
      onEliminado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-danger)' }}>
            <Trash2 size={20} /> Eliminar Registro
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-danger"><AlertCircle size={16} /> <strong>Esta acción no se puede deshacer.</strong></div>
          
          <div style={{ padding: 12, background: 'var(--color-gray-1)', borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-3)', marginBottom: 4 }}>Registro a eliminar:</div>
            <div style={{ fontWeight: 600 }}>{new Date(registro.fecha).toLocaleDateString('es-ES')} · {registro.hora} · {registro.zona}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-2)', marginTop: 4 }}>
              T. actual: {registro.temperatura_actual != null ? `${registro.temperatura_actual}°C` : '—'} ·
              Humedad: {registro.humedad != null ? `${registro.humedad}%` : '—'}
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">🔐 Confirma con tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Tu contraseña actual" />
          </div>
          
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleEliminar} disabled={loading || !password}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <Trash2 size={16} />}
            Eliminar definitivamente
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Revertir informe a borrador (admin + contraseña)
// ═══════════════════════════════════════════════════════════════
function ModalRevertirInforme({ informe, onClose, onRevertido }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRevertir = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      // Verificamos contraseña haciendo una llamada al endpoint de reset-estado
      // Como reset-estado no pide contraseña, validamos antes con un PUT al usuario
      // Forma más simple: si el reset-estado falla, error
      await api.put(`/informes/mensuales/${informe.id}/reset-estado`, { nuevo_estado: 'borrador', password });
      onRevertido();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al revertir');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-warning)' }}>
            <RotateCcw size={20} /> Revertir a Borrador
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning"><AlertCircle size={16} /> 
            El informe volverá a estado "Borrador". Si ya se había enviado solicitud de firma, esta seguirá visible pero podrás cancelarla manualmente.
          </div>
          
          <div style={{ padding: 12, background: 'var(--color-gray-1)', borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary)', fontWeight: 700 }}>{informe.codigo}</div>
            <div style={{ fontSize: '0.9rem', marginTop: 4 }}>{informe.titulo}</div>
          </div>
          
          <div className="form-group">
            <label className="form-label">🔐 Confirma con tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Tu contraseña actual" />
          </div>
          
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleRevertir} disabled={loading || !password}
            style={{ background: 'var(--color-warning)' }}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <RotateCcw size={16} />}
            Revertir a borrador
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Helpers visuales
// ═══════════════════════════════════════════════════════════════
function StatBox({ label, valor, color }) {
  return (
    <div style={{ padding: 14, background: 'var(--color-gray-1)', borderRadius: 8, border: `2px solid ${color}22`, borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: color, fontFamily: 'var(--font-mono)' }}>{valor}</div>
    </div>
  );
}

function EstadoBadge({ estado }) {
  const config = {
    borrador: { bg: '#dbeafe', color: '#1e40af', label: '📝 Borrador' },
    pendiente_firma: { bg: '#fef3c7', color: '#92400e', label: '⏳ Pdte. firma' },
    firmado: { bg: '#dcfce7', color: '#166534', label: '✅ Firmado' },
    rechazado: { bg: '#fecaca', color: '#991b1b', label: '❌ Rechazado' }
  };
  const c = config[estado] || config.borrador;
  return (
    <span style={{ padding: '3px 10px', borderRadius: 12, background: c.bg, color: c.color, fontSize: '0.75rem', fontWeight: 600 }}>
      {c.label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL ORIGINAL: Nuevo registro de temperatura
// ═══════════════════════════════════════════════════════════════
function ModalRegistro({ onClose, onGuardado }) {
  const today = new Date().toISOString().split('T')[0];
  const now = new Date().toTimeString().slice(0, 5);
  const [form, setForm] = useState({
    fecha: today, hora: now, zona: 'Almacén principal',
    temperatura_min: '', temperatura_max: '', temperatura_actual: '',
    humedad: '', observaciones: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGuardar = async () => {
    if (!form.fecha || !form.hora) { setError('Fecha y hora son obligatorias'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.post('/registros/temperatura', form);
      setSavedId(res.data.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Thermometer size={20} color="var(--color-primary)" /> Nuevo Registro de Temperatura
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {savedId && (
            <div className="alert alert-success">
              <CheckCircle2 size={16} /> Registro guardado. Ahora puedes adjuntar la hoja firmada.
            </div>
          )}
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Fecha <span className="required">*</span></label>
              <input type="date" className="form-control" value={form.fecha} onChange={e => set('fecha', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">Hora <span className="required">*</span></label>
              <input type="time" className="form-control" value={form.hora} onChange={e => set('hora', e.target.value)} disabled={!!savedId} /></div>
          </div>
          <div className="form-group">
            <label className="form-label">Zona</label>
            <select className="form-control" value={form.zona} onChange={e => set('zona', e.target.value)} disabled={!!savedId}>
              <option>Almacén principal</option><option>Cámara fría</option>
              <option>Oficina</option><option>Zona recepción</option>
            </select>
          </div>
          <div className="form-grid-3">
            <div className="form-group"><label className="form-label">T. Mínima (°C)</label>
              <input type="number" step="0.1" className="form-control" placeholder="15.0" value={form.temperatura_min} onChange={e => set('temperatura_min', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">T. Máxima (°C)</label>
              <input type="number" step="0.1" className="form-control" placeholder="25.0" value={form.temperatura_max} onChange={e => set('temperatura_max', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">T. Actual (°C)</label>
              <input type="number" step="0.1" className="form-control" placeholder="20.0" value={form.temperatura_actual} onChange={e => set('temperatura_actual', e.target.value)} disabled={!!savedId} /></div>
          </div>
          <div className="form-group"><label className="form-label">Humedad (%)</label>
            <input type="number" step="0.1" className="form-control" placeholder="50" value={form.humedad} onChange={e => set('humedad', e.target.value)} disabled={!!savedId} /></div>
          <div className="form-group"><label className="form-label">Observaciones</label>
            <textarea className="form-control" rows={3} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} disabled={!!savedId} /></div>

          <FileAttachments
            seccion="temperatura" entidadTipo="temperatura" entidadId={savedId}
            label="Adjuntar hoja de registro firmada (PDF/imagen)"
          />
        </div>
        <div className="modal-footer">
          {!savedId ? (
            <>
              <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleGuardar} disabled={loading}>
                {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
                Guardar Registro
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-outline" onClick={onClose}>Cerrar sin más archivos</button>
              <button className="btn btn-primary" onClick={onGuardado}>
                <CheckCircle2 size={16} /> Finalizar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FilaRegistro({ r, isAdmin, onEditar, onEliminar }) {
  const [expanded, setExpanded] = useState(false);

  const estadoIcon = (estado) => {
    if (estado === 'fuera_rango') return <span style={{ color: 'var(--color-danger)', fontWeight: 600, fontSize: '0.8rem' }}>⚠ Fuera rango</span>;
    if (estado === 'incidencia') return <span style={{ color: 'var(--color-warning)', fontWeight: 600, fontSize: '0.8rem' }}>⚡ Incidencia</span>;
    return <span style={{ color: 'var(--color-success)', fontSize: '0.8rem' }}>✓ Correcto</span>;
  };

  return (
    <>
      <tr style={{ background: r.estado === 'fuera_rango' ? 'rgba(220,38,38,.04)' : '' }}>
        <td>{new Date(r.fecha).toLocaleDateString('es-ES')}</td>
        <td style={{ fontFamily: 'var(--font-mono)' }}>{r.hora}</td>
        <td>{r.zona}</td>
        <td style={{ fontFamily: 'var(--font-mono)' }}>{r.temperatura_min != null ? `${r.temperatura_min}°C` : '—'}</td>
        <td style={{ fontFamily: 'var(--font-mono)' }}>{r.temperatura_max != null ? `${r.temperatura_max}°C` : '—'}</td>
        <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{r.temperatura_actual != null ? `${r.temperatura_actual}°C` : '—'}</td>
        <td style={{ fontFamily: 'var(--font-mono)' }}>{r.humedad != null ? `${r.humedad}%` : '—'}</td>
        <td style={{ fontSize: '0.82rem' }}>{r.operario_nombre}</td>
        <td>{estadoIcon(r.estado)}</td>
        <td style={{ fontSize: '0.78rem', color: 'var(--color-text-3)', maxWidth: 140 }}>{r.observaciones || '—'}</td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FileAttachments seccion="temperatura" entidadTipo="temperatura" entidadId={r.id} compact />
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setExpanded(!expanded)} title="Ver/añadir adjuntos">
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {isAdmin && (
              <>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEditar(r)} title="Editar registro (admin)"
                  style={{ color: '#3b82f6' }}>
                  <Pencil size={13} />
                </button>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEliminar(r)} title="Eliminar registro (admin)"
                  style={{ color: 'var(--color-danger)' }}>
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={11} style={{ padding: '0 14px 14px', background: 'var(--color-gray-1)' }}>
            <FileAttachments seccion="temperatura" entidadTipo="temperatura" entidadId={r.id} label="Subir hoja de registro firmada" />
          </td>
        </tr>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL
// ═══════════════════════════════════════════════════════════════
export default function TemperaturaPage() {
  const { canSolicitarFirma } = useAuth();
  const [registros, setRegistros] = useState([]);
  const [informes, setInformes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalRegistro, setModalRegistro] = useState(false);
  const [modalInforme, setModalInforme] = useState(false);
  const [verInformeId, setVerInformeId] = useState(null);
  const [eliminarInforme, setEliminarInforme] = useState(null);
  const [editarRegistro, setEditarRegistro] = useState(null);
  const [eliminarRegistro, setEliminarRegistro] = useState(null);
  const [revertirInforme, setRevertirInforme] = useState(null);
  const [success, setSuccess] = useState('');
  // Filtro vacío al entrar → muestra TODOS los registros. El usuario
  // pone fechas y pulsa "Filtrar" solo si quiere acotar la búsqueda.
  const [filtros, setFiltros] = useState({ desde: '', hasta: '' });
  
  // Detectar si es admin desde el token JWT (por username, ya que el token no lleva rol)
  const isAdmin = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username === 'admin' || payload.rol === 'administrador' || payload.rol_nombre === 'administrador';
    } catch { return false; }
  })();

  const loadRegistros = () => {
    api.get('/registros/temperatura', { params: filtros }).then(r => setRegistros(r.data));
  };
  
  const loadInformes = () => {
    api.get('/informes/mensuales', { params: { tipo: 'temperatura' } }).then(r => setInformes(r.data));
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/registros/temperatura', { params: filtros }),
      api.get('/informes/mensuales', { params: { tipo: 'temperatura' } })
    ])
      .then(([reg, inf]) => { setRegistros(reg.data); setInformes(inf.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleInformeGenerado = (informe) => {
    setModalInforme(false);
    setSuccess(`Informe ${informe.codigo} generado correctamente.`);
    loadInformes();
    setTimeout(() => setSuccess(''), 5000);
  };

  const handlePasarPdteFirma = async (informe) => {
    if (!window.confirm(`¿Marcar el informe ${informe.codigo} como "Pendiente de firma"?\n\nDespués podrás enviar la solicitud a la Directora Técnica desde el módulo de Firmas.`)) return;
    try {
      await api.put(`/informes/mensuales/${informe.id}/pasar-pdte-firma`);
      setSuccess(`Informe ${informe.codigo} marcado como pendiente de firma. Ahora ve a Firmas Pendientes.`);
      loadInformes();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      alert(err.response?.data?.error || 'Error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Thermometer size={24} color="var(--color-primary)" />Control de Temperatura</h1>
          <p>{registros.length} registros · {informes.length} informe(s) mensual(es)</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-outline" onClick={() => setModalInforme(true)}>
            <FileText size={16} /> Generar Informe Mensual
          </button>
          <button className="btn btn-primary" onClick={() => setModalRegistro(true)}><Plus size={16} /> Nuevo Registro</button>
        </div>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}><CheckCircle2 size={16} /> {success}</div>}

      {/* ── SECCIÓN: INFORMES MENSUALES ──────────────────────── */}
      {informes.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileText size={18} color="var(--color-primary)" />
              Informes Mensuales
            </h3>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-3)' }}>{informes.length} informe(s)</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Período</th>
                  <th>Registros</th>
                  <th>Incidencias</th>
                  <th>Estado</th>
                  <th>Creado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {informes.map(inf => (
                  <tr key={inf.id}>
                    <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary)', fontWeight: 600 }}>{inf.codigo}</td>
                    <td>{MESES[inf.mes - 1]} {inf.anio}</td>
                    <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{inf.total_registros}</td>
                    <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', color: inf.incidencias > 0 ? 'var(--color-danger)' : 'var(--color-success)', fontWeight: 600 }}>
                      {inf.incidencias}
                    </td>
                    <td><EstadoBadge estado={inf.estado} />
                      {inf.incidencias > 0 && inf.estado === 'borrador' && canSolicitarFirma() && (
                        <div style={{
                          marginTop: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          background: '#fef2f2',
                          border: '1px solid #fca5a5',
                          color: '#b91c1c',
                          padding: '3px 9px',
                          borderRadius: 20,
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                        title={`Este informe tiene ${inf.incidencias} registro(s) fuera de rango. Conviene enviarlo a firmar a la Directora Técnica.`}>
                          ✈️ {inf.incidencias} fuera de rango — enviar a firmar
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--color-text-3)' }}>
                      {new Date(inf.created_at).toLocaleDateString('es-ES')}
                      <div style={{ fontSize: '0.75rem' }}>{inf.creado_nombre}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setVerInformeId(inf.id)} title="Ver detalle">
                          <Eye size={14} />
                        </button>
                        {inf.estado === 'borrador' && (
                          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => handlePasarPdteFirma(inf)} title="Pasar a Pdte. firma"
                            style={{ color: 'var(--color-warning)' }}>
                            <Send size={14} />
                          </button>
                        )}
                        {inf.estado === 'pendiente_firma' && isAdmin && (
                          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setRevertirInforme(inf)} title="Revertir a borrador (admin)"
                            style={{ color: 'var(--color-warning)' }}>
                            <RotateCcw size={14} />
                          </button>
                        )}
                        {inf.estado === 'borrador' && (
                          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setEliminarInforme(inf)} title="Eliminar"
                            style={{ color: 'var(--color-danger)' }}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SECCIÓN: REGISTROS DIARIOS ───────────────────────── */}
      <div className="card" style={{ marginBottom: 16, padding: '14px 20px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0 }}><label className="form-label">Desde</label>
            <input type="date" className="form-control" value={filtros.desde} onChange={e => setFiltros(f => ({ ...f, desde: e.target.value }))} /></div>
          <div className="form-group" style={{ margin: 0 }}><label className="form-label">Hasta</label>
            <input type="date" className="form-control" value={filtros.hasta} onChange={e => setFiltros(f => ({ ...f, hasta: e.target.value }))} /></div>
          <button className="btn btn-outline" onClick={loadRegistros}>Filtrar</button>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="loading-overlay"><div className="spinner" /></div> :
          registros.length === 0 ? (
            <div className="empty-state"><Thermometer size={48} /><h3>Sin registros</h3></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th><th>Hora</th><th>Zona</th>
                    <th>T. Mín</th><th>T. Máx</th><th>T. Actual</th>
                    <th>Humedad</th><th>Operario</th><th>Estado</th>
                    <th>Observaciones</th><th><Paperclip size={13} /></th>
                  </tr>
                </thead>
                <tbody>
                  {registros.map(r => <FilaRegistro key={r.id} r={r} isAdmin={isAdmin} onEditar={setEditarRegistro} onEliminar={setEliminarRegistro} />)}
                </tbody>
              </table>
            </div>
          )
        }
      </div>

      {modalRegistro && <ModalRegistro onClose={() => setModalRegistro(false)} onGuardado={() => { setModalRegistro(false); loadRegistros(); }} />}
      {modalInforme && <ModalGenerarInforme onClose={() => setModalInforme(false)} onGenerado={handleInformeGenerado} />}
      {verInformeId && <ModalVerInforme informeId={verInformeId} onClose={() => setVerInformeId(null)} />}
      {eliminarInforme && <ModalEliminarInforme informe={eliminarInforme} onClose={() => setEliminarInforme(null)} onEliminado={() => { setEliminarInforme(null); loadInformes(); setSuccess('Informe eliminado'); setTimeout(() => setSuccess(''), 3000); }} />}
      {editarRegistro && <ModalEditarRegistro registro={editarRegistro} onClose={() => setEditarRegistro(null)} onActualizado={() => { setEditarRegistro(null); loadRegistros(); setSuccess('Registro actualizado'); setTimeout(() => setSuccess(''), 3000); }} />}
      {eliminarRegistro && <ModalEliminarRegistro registro={eliminarRegistro} onClose={() => setEliminarRegistro(null)} onEliminado={() => { setEliminarRegistro(null); loadRegistros(); setSuccess('Registro eliminado'); setTimeout(() => setSuccess(''), 3000); }} />}
      {revertirInforme && <ModalRevertirInforme informe={revertirInforme} onClose={() => setRevertirInforme(null)} onRevertido={() => { setRevertirInforme(null); loadInformes(); setSuccess('Informe revertido a Borrador'); setTimeout(() => setSuccess(''), 3000); }} />}
    </div>
  );
}
